import React, { FunctionComponent, useState } from 'react';
import { Button, Divider, Form, FormInstance, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { StyledButton } from '../components/Button';
import { StyledRow } from '../components/StyledRow';
import { AddIcon } from '../components/TableWithDataUpdate';
import { useAddRecruitment } from '../features/client-details/useAddRecruitment';
import { SaveButtonIcon } from '../features/dictionaries/DictionaryItemsList';
import { useCloneRecruitments } from '../features/project-details/useCloneRecruitments';
import { useUpdateRecruitmentNote } from '../features/recruitment-details/useUpdateRecruitmentNote';
import {
  RecruitmentsDataType,
  useRecruitmentsTableDataSource,
} from '../features/recruitments/useRecruitmentsTableDataSource';
import { useSearchRecruitments } from '../features/recruitments/useSearchRecruitments';
import Icon from '../icons/Icon';

import { ActiveFilter, ActiveRadioGroup } from './ActiveRadioGroup';
import { handleFormErrors } from './errorHelpers';
import { mapFilterToSearchQuery } from './filterHelpers';
import { Filter } from './filters.type';
import { SearchFormFieldEnum } from './searchFormField.types';
import { StyledLinkedTable } from './tableView.styles';
import { StatusType } from './types';
import { useTableDataFilters } from './useTableDataFilters';

const ActionBar = styled(StyledRow)`
  justify-content: space-between;
  margin-bottom: 15px;
  margin-top: 15px;
`;

const DefaultHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 992px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 15px;
  gap: 5px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }
`;

const Title: React.FC = () => (
  <Typography.Title level={5} style={{ paddingLeft: '15px' }}>
    Recruitments
  </Typography.Title>
);

const EditButtons: React.FC<{ handleCancel: () => void; handleSave: () => void }> = ({ handleCancel, handleSave }) => {
  return (
    <StyledRow flexGrow="0">
      <StyledButton onClick={handleCancel}>Cancel</StyledButton>
      <StyledButton type="primary" onClick={handleSave} icon={<SaveButtonIcon />}>
        Save
      </StyledButton>
    </StyledRow>
  );
};

const AddElementsToCloneMessage: React.FC = () => (
  <StyledRow justifyContent="flex-start" gap="0.3rem">
    <Icon name="ArrowCircleDown" />
    <Typography.Title level={5} style={{ paddingLeft: '15px' }}>
      At first you need to choose recruitment to duplicate to another project
    </Typography.Title>
  </StyledRow>
);

type FilteredRecruitmentsTableProps = {
  defaultFilter: Filter;
  itemForm: FunctionComponent<{ form: FormInstance }>;
  duplicateForm?: FunctionComponent<{ form: FormInstance }>;
  getColumnsDefinition: ({
    sortedInfo,
    withLocalSorting,
  }: {
    sortedInfo: { field: string; order: string }[];
    withLocalSorting?: boolean;
  }) => ColumnsType<RecruitmentsDataType>;
  duplicateButtonName?: string;
  withClone?: boolean;
  defaultActiveFilter?: ActiveFilter;
  mapErrorFieldsToFormLabels: Record<string, string>;
  mapRecordToSavePayload: (record: { candidateUuid: string; projectUuid: string; responsible: string; status: StatusType }) => {
    data: {
      candidateUuid: string;
      projectUuid: string;
      responsible: string;
      status: StatusType;
    };
  };
};

export const FilteredRecruitmentsTable: React.FC<FilteredRecruitmentsTableProps> = ({
  defaultFilter,
  itemForm: ItemForm,
  duplicateForm: DuplicateForm,
  getColumnsDefinition,
  mapRecordToSavePayload,
  mapErrorFieldsToFormLabels,
  duplicateButtonName,
  withClone,
  defaultActiveFilter = ActiveFilter.ALL,
}) => {
  const addRecruitment = useAddRecruitment();
  const addRecruitmentNote = useUpdateRecruitmentNote();
  const cloneRecruitments = useCloneRecruitments();

  const [form] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const [isEditing, setIsEditing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [recruitmentsToDuplicate, setRecruitmentsToDuplicate] = useState<React.Key[]>();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(defaultActiveFilter);

  const { currentFilter, pagingFrom, pageNumber, pageSize, handleChangeSortingAndPagination } =
    useTableDataFilters(defaultFilter);

  const columns = getColumnsDefinition({ sortedInfo: currentFilter.sort });
  const { data } = useSearchRecruitments({
    ...mapFilterToSearchQuery(currentFilter, pagingFrom, pageSize),
    criteria: [
      ...currentFilter.criteria,
      ...(activeFilter !== ActiveFilter.ALL
        ? [
            {
              field: SearchFormFieldEnum.StatusActionRequired,
              value: (activeFilter !== ActiveFilter.INACTIVE).toString(),
            },
          ]
        : []),
    ],
  });

  const dataSource = useRecruitmentsTableDataSource(data);
  const pagination = {
    pageSize,
    total: data?.count,
    current: pageNumber,
    position: ['none', 'bottomCenter'],
    hideOnSinglePage: true,
  };

  const dataFilteredByActive = dataSource.filter((element) => {
    if (element.actionRequired) {
      return [ActiveFilter.ACTIVE, ActiveFilter.ALL].includes(activeFilter);
    }
    return [ActiveFilter.INACTIVE, ActiveFilter.ALL].includes(activeFilter);
  });

  const handleActiveFilterChange = (filter: ActiveFilter) => {
    handleChangeSortingAndPagination({
      current: 1,
      pageSize,
      total: data?.count,
    });
    setActiveFilter(filter);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsDuplicating(false);
  };

  const handleAddRecruitments = () => {
    form.resetFields();
    setIsEditing(true);
  };

  const handleCloneRecruitments = () => {
    cloneForm.resetFields();
    setIsDuplicating(true);
  };

  const handleSaveNewRecruitment = () => {
    const record = form.getFieldsValue(true) as {
      candidateUuid: string;
      projectUuid: string;
      responsible: string;
      status: StatusType;
      content?: string;
    };
    addRecruitment.mutate(mapRecordToSavePayload(record), {
      onSuccess: (newRecruitment) => {
        if (record.content) {
          addRecruitmentNote.mutate({
            queryParams: { id: newRecruitment.data.uuid },
            data: { content: record.content },
          });
        }
        setIsEditing(false);
      },
      onError: (error) => handleFormErrors({ error, form, mapErrorFieldsToFormLabels }),
    });
  };

  const handleSaveClonedRecruitments = () => {
    const record = cloneForm.getFieldsValue(true);
    cloneRecruitments.mutate(
      {
        data: {
          note: record.content,
          project: record['projectUuid'],
          recruitments: recruitmentsToDuplicate,
          status: record.status,
          responsible: record.responsible,
        },
      },
      {
        onSuccess: () => setIsDuplicating(false),
        onError: (error) => handleFormErrors({ error, form: cloneForm, mapErrorFieldsToFormLabels }),
      }
    );
  };

  const ActionButtons = () => (
    <ActionButtonsContainer>
      {withClone && (
        <Button icon={<AddIcon />} onClick={handleCloneRecruitments}>
          {duplicateButtonName}
        </Button>
      )}

      <Button icon={<AddIcon />} onClick={handleAddRecruitments}>
        Add recruitment
      </Button>

      <FilterByActive />
    </ActionButtonsContainer>
  );

  const FilterByActive = () => <ActiveRadioGroup activeTab={activeFilter} changeActiveTab={handleActiveFilterChange} />;

  const Header: React.FC = () => {
    return (
      <div>
        {!isDuplicating && !isEditing && (
          <DefaultHeaderContainer>
            <Title />
            <ActionButtons />
          </DefaultHeaderContainer>
        )}

        {isEditing && (
          <>
            <ActionBar>
              <Title />
              <EditButtons handleCancel={handleCancel} handleSave={handleSaveNewRecruitment} />
            </ActionBar>
            <ItemForm form={form} />
            <Divider />
          </>
        )}

        {isDuplicating && DuplicateForm && (
          <>
            <ActionBar>
              <Title />
              <StyledRow flexGrow="0">
                <FilterByActive />
              </StyledRow>
            </ActionBar>
            <StyledRow justifyContent="space-between">
              <Typography.Text strong>Duplicate to another project</Typography.Text>
              <EditButtons handleCancel={handleCancel} handleSave={handleSaveClonedRecruitments} />
            </StyledRow>
            {recruitmentsToDuplicate?.length ? (
              <>
                <DuplicateForm form={cloneForm} />
                <Divider style={{ marginTop: '5px', marginBottom: '10px' }} />
              </>
            ) : (
              <AddElementsToCloneMessage />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: '15px' }}>
      <Header />
      <StyledLinkedTable
        columns={columns}
        dataSource={dataFilteredByActive}
        scroll={{ y: 700 }}
        bordered={true}
        pagination={pagination}
        rowSelection={
          withClone && {
            type: 'checkbox',
            onChange: (selectedRowKeys: React.Key[]) => {
              setRecruitmentsToDuplicate(selectedRowKeys);
            },
          }
        }
        onChange={handleChangeSortingAndPagination}
      />
    </div>
  );
};
